import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react'

import sidebarChevronUrl from '@/assets/admin-sidebar/chevron.svg'
import type { PortalKind } from '@/lib/auth/token-storage'
import { cn } from '@/lib/cn'

import type { SidebarNavItem, SidebarNavSearchItem } from './navigation-types'

interface SidebarScrollbarState {
  thumbHeight: number
  thumbTop: number
  visible: boolean
}

interface CollapsedFlyoutState {
  key: string
  top: number
}

interface SidebarActionMenuState {
  key: string
  left: number
  top: number
}

interface SidebarSearchMenuState {
  key: string
  left: number
  top: number
}

const sidebarSearchMenuChromeHeight = 58
const sidebarSearchMenuListMaxHeight = 244
const sidebarExpandedGroupsStorageKeyPrefix = 'aihub_sidebar_expanded_groups'
const sidebarScrollStorageKeyPrefix = 'aihub_sidebar_scroll_position'
const sidebarExpandedGroups = new Map<string, Record<string, boolean>>()
const sidebarScrollPositions = new Map<string, number>()

function getDefaultExpandedGroups(items: SidebarNavItem[]): Record<string, boolean> {
  return Object.fromEntries(
    items
      .filter((item) => item.children !== undefined)
      .map((item) => [item.key, item.defaultExpanded ?? false]),
  )
}

function mergeExpandedGroups(
  items: SidebarNavItem[],
  storedGroups?: Record<string, boolean>,
): Record<string, boolean> {
  const defaultGroups = getDefaultExpandedGroups(items)

  return Object.fromEntries(
    Object.entries(defaultGroups).map(([key, defaultExpanded]) => [
      key,
      typeof storedGroups?.[key] === 'boolean' ? storedGroups[key] : defaultExpanded,
    ]),
  )
}

function readExpandedGroups(items: SidebarNavItem[], persistenceKey?: string) {
  if (!persistenceKey) {
    return getDefaultExpandedGroups(items)
  }

  if (typeof window === 'undefined') {
    return mergeExpandedGroups(items, sidebarExpandedGroups.get(persistenceKey))
  }

  const storageKey = `${sidebarExpandedGroupsStorageKeyPrefix}:${persistenceKey}`
  let serializedValue: string | null

  try {
    serializedValue = window.sessionStorage.getItem(storageKey)
  } catch {
    return mergeExpandedGroups(items, sidebarExpandedGroups.get(persistenceKey))
  }

  if (serializedValue === null) {
    sidebarExpandedGroups.delete(persistenceKey)
    return getDefaultExpandedGroups(items)
  }

  try {
    const storedValue = JSON.parse(serializedValue) as unknown

    if (!storedValue || typeof storedValue !== 'object' || Array.isArray(storedValue)) {
      sidebarExpandedGroups.delete(persistenceKey)
      return getDefaultExpandedGroups(items)
    }

    const expandedGroups = mergeExpandedGroups(items, storedValue as Record<string, boolean>)

    sidebarExpandedGroups.set(persistenceKey, expandedGroups)
    return expandedGroups
  } catch {
    sidebarExpandedGroups.delete(persistenceKey)
    return getDefaultExpandedGroups(items)
  }
}

function writeExpandedGroups(persistenceKey: string, expandedGroups: Record<string, boolean>) {
  sidebarExpandedGroups.set(persistenceKey, expandedGroups)

  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(
      `${sidebarExpandedGroupsStorageKeyPrefix}:${persistenceKey}`,
      JSON.stringify(expandedGroups),
    )
  } catch {
    // 受限存储环境下退回内存记录，当前 SPA 运行期内仍可恢复。
  }
}

function readSidebarScrollPosition(persistenceKey: string) {
  const memoryValue = sidebarScrollPositions.get(persistenceKey)

  if (typeof memoryValue === 'number') {
    return memoryValue
  }

  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const storedValue = Number(
      window.sessionStorage.getItem(`${sidebarScrollStorageKeyPrefix}:${persistenceKey}`),
    )

    return Number.isFinite(storedValue) && storedValue >= 0 ? storedValue : undefined
  } catch {
    return undefined
  }
}

function writeSidebarScrollPosition(persistenceKey: string, scrollTop: number) {
  sidebarScrollPositions.set(persistenceKey, scrollTop)

  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(
      `${sidebarScrollStorageKeyPrefix}:${persistenceKey}`,
      String(scrollTop),
    )
  } catch {
    // 受限存储环境下退回内存记录，当前 SPA 会话内仍可恢复。
  }
}

function NavChevronIcon({ open, strong = false }: { open: boolean; strong?: boolean }) {
  return (
    <img
      alt=""
      aria-hidden
      className={cn(
        'h-1.5 w-2.5 transition-transform duration-[180ms] ease-out motion-reduce:transition-none',
        strong ? 'opacity-100' : 'opacity-70',
        open ? 'rotate-0' : '-rotate-90',
      )}
      src={sidebarChevronUrl}
    />
  )
}

function isNavItemActive(item: SidebarNavItem, activePath?: string) {
  if (item.kind === 'divider' || item.kind === 'heading' || item.kind === 'spacer') {
    return false
  }

  if (item.activeBehavior === 'never') {
    return false
  }

  if (!activePath) {
    return false
  }

  if (item.activeMatch === 'hub-chat') {
    if (activePath === '/chat') {
      return true
    }

    if (!activePath.startsWith('/chat?')) {
      return false
    }

    const search = new URLSearchParams(activePath.split('?')[1] ?? '')
    return !search.has('appId')
  }

  if (item.path) {
    const hasQuery = item.path.includes('?')

    if (
      activePath === item.path ||
      activePath.startsWith(`${item.path}/`) ||
      (hasQuery && activePath.startsWith(`${item.path}&`))
    ) {
      return true
    }
  }

  return (
    item.children?.some(
      (child) => activePath === child.path || activePath.startsWith(`${child.path}/`),
    ) ?? false
  )
}

function getCollapsedFlyoutTop(itemTop: number, childCount: number) {
  const panelHeight = 40 + childCount * 36 + 12
  const viewportPadding = 12
  const maxTop = window.innerHeight - panelHeight - viewportPadding

  return Math.max(viewportPadding, Math.min(itemTop, maxTop))
}

function getActionMenuPlacement(rect: DOMRect, actionCount: number) {
  const menuWidth = 156
  const menuHeight = 12 + actionCount * 40
  const viewportPadding = 12
  const left = Math.min(rect.right + 8, window.innerWidth - menuWidth - viewportPadding)
  const maxTop = window.innerHeight - menuHeight - viewportPadding

  return {
    left: Math.max(viewportPadding, left),
    top: Math.max(viewportPadding, Math.min(rect.top - 8, maxTop)),
  }
}

function getSearchMenuPlacement(rect: DOMRect, itemCount: number) {
  const menuWidth = 274
  const menuHeight = Math.min(
    sidebarSearchMenuChromeHeight + sidebarSearchMenuListMaxHeight,
    sidebarSearchMenuChromeHeight + itemCount * 36,
  )
  const viewportPadding = 12
  const left = Math.min(rect.right + 12, window.innerWidth - menuWidth - viewportPadding)
  const maxTop = window.innerHeight - menuHeight - viewportPadding

  return {
    left: Math.max(viewportPadding, left),
    top: Math.max(viewportPadding, Math.min(rect.top, maxTop)),
  }
}

function parseInternalPath(path: string) {
  const [pathname, searchString = ''] = path.split('?')
  const search = Object.fromEntries(new URLSearchParams(searchString))

  return {
    pathname,
    search,
  }
}

function isCurrentBrowserPath(path: string) {
  const target = new URL(path, window.location.href)

  return (
    window.location.pathname === target.pathname &&
    window.location.search === target.search &&
    window.location.hash === target.hash
  )
}

function MagnifierIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M7.25 11.5a4.25 4.25 0 1 1 0-8.5 4.25 4.25 0 0 1 0 8.5Zm3.05-1.2 2.2 2.2"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function ExternalLinkIcon() {
  return (
    <svg aria-hidden className="size-3.5" fill="none" viewBox="0 0 14 14">
      <path
        d="M5 3h6v6M11 3 4 10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.25"
      />
    </svg>
  )
}

function MoreHorizontalIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 24 24">
      <path
        d="M7 12h.01M12 12h.01M17 12h.01"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.6"
      />
    </svg>
  )
}

function useSidebarScrollbar(enabled: boolean, persistenceKey?: string) {
  const scrollRef = useRef<HTMLElement | null>(null)
  const [scrollbar, setScrollbar] = useState<SidebarScrollbarState>({
    thumbHeight: 0,
    thumbTop: 0,
    visible: false,
  })
  const restoreScrollPosition = useCallback(() => {
    const node = scrollRef.current

    if (!node || !enabled || !persistenceKey) {
      return
    }

    const persistedScrollTop = readSidebarScrollPosition(persistenceKey)

    if (typeof persistedScrollTop !== 'number') {
      return
    }

    const maxScrollTop = Math.max(0, node.scrollHeight - node.clientHeight)
    const nextScrollTop = Math.min(persistedScrollTop, maxScrollTop)

    if (Math.abs(node.scrollTop - nextScrollTop) > 0.5) {
      node.scrollTop = nextScrollTop
    }
  }, [enabled, persistenceKey])

  const syncScrollbar = useCallback(() => {
    const node = scrollRef.current

    if (!node || !enabled) {
      setScrollbar((current) =>
        current.visible ? { thumbHeight: 0, thumbTop: 0, visible: false } : current,
      )
      return
    }

    const { clientHeight, scrollHeight, scrollTop } = node
    const visible = scrollHeight > clientHeight + 1

    if (!visible) {
      setScrollbar((current) =>
        current.visible ? { thumbHeight: 0, thumbTop: 0, visible: false } : current,
      )
      return
    }

    const thumbHeight = Math.max(32, Math.round((clientHeight / scrollHeight) * clientHeight))
    const maxThumbTop = clientHeight - thumbHeight
    const maxScrollTop = scrollHeight - clientHeight
    const thumbTop = maxScrollTop > 0 ? Math.round((scrollTop / maxScrollTop) * maxThumbTop) : 0

    setScrollbar((current) => {
      if (
        current.visible === visible &&
        current.thumbHeight === thumbHeight &&
        current.thumbTop === thumbTop
      ) {
        return current
      }

      return { thumbHeight, thumbTop, visible }
    })
  }, [enabled])
  const rememberScrollPosition = useCallback(
    (scrollTop: number) => {
      if (persistenceKey) {
        writeSidebarScrollPosition(persistenceKey, scrollTop)
      }
    },
    [persistenceKey],
  )
  const setScrollNode = useCallback(
    (node: HTMLElement | null) => {
      scrollRef.current = node
      restoreScrollPosition()
    },
    [restoreScrollPosition],
  )
  const rememberCurrentScrollPosition = useCallback(() => {
    const node = scrollRef.current

    if (node) {
      rememberScrollPosition(node.scrollTop)
    }
  }, [rememberScrollPosition])

  useLayoutEffect(() => {
    restoreScrollPosition()
    syncScrollbar()
    const frameId = window.requestAnimationFrame(() => {
      restoreScrollPosition()
      syncScrollbar()
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [restoreScrollPosition, syncScrollbar])

  useEffect(() => {
    syncScrollbar()

    const node = scrollRef.current

    if (!node || !enabled) {
      return undefined
    }

    window.addEventListener('resize', syncScrollbar)

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        window.removeEventListener('resize', syncScrollbar)
      }
    }

    const resizeObserver = new ResizeObserver(syncScrollbar)
    resizeObserver.observe(node)

    if (node.firstElementChild) {
      resizeObserver.observe(node.firstElementChild)
    }

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', syncScrollbar)
    }
  }, [enabled, syncScrollbar])

  return {
    rememberCurrentScrollPosition,
    rememberScrollPosition,
    setScrollNode,
    scrollbar,
    syncScrollbar,
  }
}

function SidebarScrollbar({
  collapsed,
  scrollbar,
  variant,
}: {
  collapsed: boolean
  scrollbar: SidebarScrollbarState
  variant: 'bottom' | 'single' | 'top'
}) {
  if (!scrollbar.visible) {
    return null
  }

  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute bottom-0 top-0 z-10 w-1.5 opacity-0 transition-opacity duration-150',
        collapsed ? 'right-0.5' : 'right-1',
        variant === 'top'
          ? 'group-hover/sidebar-scroll-top:opacity-100'
          : variant === 'bottom'
            ? 'group-hover/sidebar-scroll-bottom:opacity-100'
            : 'group-hover/sidebar-scroll:opacity-100',
      )}
      data-testid={
        variant === 'single'
          ? 'sidebar-scrollbar'
          : variant === 'top'
            ? 'sidebar-top-scrollbar'
            : 'sidebar-bottom-scrollbar'
      }
    >
      <div
        className="absolute right-0 w-1.5 rounded-full bg-[rgba(102,112,133,0.36)]"
        data-testid={
          variant === 'single'
            ? 'sidebar-scrollbar-thumb'
            : variant === 'top'
              ? 'sidebar-top-scrollbar-thumb'
              : 'sidebar-bottom-scrollbar-thumb'
        }
        style={{
          height: `${String(scrollbar.thumbHeight)}px`,
          transform: `translateY(${String(scrollbar.thumbTop)}px)`,
        }}
      />
    </div>
  )
}

export function SidebarNavigation({
  activePath,
  collapsed,
  expandedGroupsPersistenceKey,
  items,
  portal = 'user',
}: {
  activePath?: string
  collapsed: boolean
  expandedGroupsPersistenceKey?: string
  items: SidebarNavItem[]
  portal?: PortalKind
}) {
  const navigate = useNavigate()
  const navigationRootRef = useRef<HTMLElement | null>(null)
  const flyoutRef = useRef<HTMLDivElement | null>(null)
  const actionMenuRef = useRef<HTMLDivElement | null>(null)
  const searchMenuRef = useRef<HTMLDivElement | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    readExpandedGroups(items, expandedGroupsPersistenceKey),
  )
  const currentExpandedGroups = mergeExpandedGroups(items, expandedGroups)
  const [collapsedFlyout, setCollapsedFlyout] = useState<CollapsedFlyoutState | null>(null)
  const [actionMenu, setActionMenu] = useState<SidebarActionMenuState | null>(null)
  const [searchMenu, setSearchMenu] = useState<SidebarSearchMenuState | null>(null)
  const [searchMenuValue, setSearchMenuValue] = useState('')
  const splitDividerIndex = items.findIndex(
    (item) => item.kind === 'divider' && item.key === 'conversation-divider',
  )
  const usesSplitLayout = portal === 'user' && !collapsed && splitDividerIndex > 0
  const {
    rememberCurrentScrollPosition: rememberCurrentSingleScrollPosition,
    rememberScrollPosition: rememberSingleScrollPosition,
    setScrollNode: setSingleScrollNode,
    scrollbar: singleScrollbar,
    syncScrollbar: syncSingleScrollbar,
  } = useSidebarScrollbar(!usesSplitLayout, `${portal}:${collapsed ? 'collapsed' : 'single'}`)
  const {
    rememberCurrentScrollPosition: rememberCurrentTopScrollPosition,
    rememberScrollPosition: rememberTopScrollPosition,
    setScrollNode: setTopScrollNode,
    scrollbar: topScrollbar,
    syncScrollbar: syncTopScrollbar,
  } = useSidebarScrollbar(usesSplitLayout, `${portal}:split-top`)
  const {
    rememberCurrentScrollPosition: rememberCurrentBottomScrollPosition,
    rememberScrollPosition: rememberBottomScrollPosition,
    setScrollNode: setBottomScrollNode,
    scrollbar: bottomScrollbar,
    syncScrollbar: syncBottomScrollbar,
  } = useSidebarScrollbar(usesSplitLayout, `${portal}:split-bottom`)
  const flyoutItem =
    collapsed && collapsedFlyout
      ? (items.find((item) => item.key === collapsedFlyout.key) ?? null)
      : null
  const flyoutChildren = flyoutItem?.children ?? []
  const actionMenuItem = actionMenu
    ? (items.find((item) => item.key === actionMenu.key) ?? null)
    : null
  const actionMenuActions = actionMenuItem?.actions ?? []
  const searchMenuItem = searchMenu
    ? (items.find((item) => item.key === searchMenu.key) ?? null)
    : null
  const searchMenuConfig = searchMenuItem?.searchMenu ?? null
  const searchTerm = searchMenuValue.trim().toLowerCase()
  const filteredSearchItems: SidebarNavSearchItem[] =
    searchMenuConfig?.filterMode === 'server'
      ? searchMenuConfig.items
      : (searchMenuConfig?.items.filter((item) => item.label.toLowerCase().includes(searchTerm)) ??
        [])
  const handleLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, path?: string, onSelect?: () => void) => {
      if (!path || !path.startsWith('/') || event.metaKey || event.ctrlKey || event.shiftKey) {
        return
      }

      event.preventDefault()
      rememberCurrentSingleScrollPosition()
      rememberCurrentTopScrollPosition()
      rememberCurrentBottomScrollPosition()
      setActionMenu(null)
      setSearchMenu(null)
      onSelect?.()
      const parsedPath = parseInternalPath(path)

      navigate({
        search: parsedPath.search as never,
        to: parsedPath.pathname as never,
      }).then(
        () => {
          if (!isCurrentBrowserPath(path)) {
            window.location.assign(path)
          }
        },
        () => {
          window.location.assign(path)
        },
      )
    },
    [
      navigate,
      rememberCurrentBottomScrollPosition,
      rememberCurrentSingleScrollPosition,
      rememberCurrentTopScrollPosition,
    ],
  )

  useEffect(() => {
    syncSingleScrollbar()
    syncTopScrollbar()
    syncBottomScrollbar()
  }, [
    collapsed,
    expandedGroups,
    items.length,
    syncBottomScrollbar,
    syncSingleScrollbar,
    syncTopScrollbar,
    usesSplitLayout,
  ])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (collapsed) {
        setActionMenu(null)
        setSearchMenu(null)
      } else {
        setCollapsedFlyout(null)
      }
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [collapsed])

  useEffect(() => {
    if (!actionMenu) {
      return undefined
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (actionMenuRef.current?.contains(target)) {
        return
      }

      if (
        target instanceof Element &&
        target.closest('[data-sidebar-action-menu-trigger="true"]')
      ) {
        return
      }

      setActionMenu(null)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActionMenu(null)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [actionMenu])

  useEffect(() => {
    if (!searchMenu) {
      return undefined
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (searchMenuRef.current?.contains(target)) {
        return
      }

      if (
        target instanceof Element &&
        target.closest('[data-sidebar-search-menu-trigger="true"]')
      ) {
        return
      }

      setSearchMenu(null)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchMenu(null)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [searchMenu])

  useEffect(() => {
    if (!collapsedFlyout) {
      return undefined
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (flyoutRef.current?.contains(target) || navigationRootRef.current?.contains(target)) {
        return
      }

      setCollapsedFlyout(null)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCollapsedFlyout(null)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [collapsedFlyout])

  const secondaryActiveClassName =
    portal === 'admin' ? 'sidebar-active-structural' : 'sidebar-active-secondary'
  const primaryActiveClassName =
    portal === 'admin' ? 'sidebar-active-card' : 'sidebar-active-primary'

  const renderNavItem = (item: SidebarNavItem) => {
    if (item.kind === 'spacer') {
      return (
        <li aria-hidden key={item.key} style={{ height: `${String(item.spacerHeight ?? 0)}px` }} />
      )
    }

    if (item.kind === 'divider') {
      return (
        <li aria-hidden className="mb-1.5" key={item.key}>
          <div
            className={cn(
              'h-px bg-[var(--color-sidebar-border-subtle)]',
              collapsed ? 'mx-2' : 'mr-[22px]',
            )}
          />
        </li>
      )
    }

    const active = isNavItemActive(item, activePath)
    const childItems = item.children ?? []
    const hasChildGroup = item.children !== undefined
    const hasChildren = childItems.length > 0
    const expanded = currentExpandedGroups[item.key]
    const label = item.label ?? ''
    const reserveIconSpace = item.icon != null || item.reserveIconSpace === true
    const isAppItem = item.variant === 'app'
    const isGhostProminentItem = item.variant === 'ghost-prominent'
    const isStrongItem = item.variant === 'strong'
    const isSubtleItem = item.variant === 'subtle' || item.variant === 'subtle-strong'
    const isProminentItem = item.variant === 'prominent' || isGhostProminentItem
    const hasActions = Boolean(item.actions?.length)
    const inlineStatus = item.inlineStatus
    const spacingBeforeClassName = item.spacingBefore === 'section' ? 'mt-4' : undefined
    const icon = item.icon ? (
      <span
        className={cn(
          'flex shrink-0 items-center justify-center justify-self-center text-current',
          isAppItem ? 'size-5' : 'size-[18px]',
        )}
      >
        {item.icon}
      </span>
    ) : reserveIconSpace ? (
      <span aria-hidden className="size-[18px] justify-self-center" />
    ) : null

    if (item.kind === 'heading') {
      if (collapsed) {
        if (hasChildren && icon) {
          return (
            <li className="flex h-12 items-center justify-center" key={item.key}>
              <button
                aria-expanded={collapsedFlyout?.key === item.key}
                aria-haspopup="menu"
                aria-label={label}
                className={cn(
                  'flex size-10 cursor-pointer items-center justify-center rounded-lg border border-transparent text-[14px] font-semibold leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                  collapsedFlyout?.key === item.key
                    ? 'sidebar-active-card-collapsed'
                    : 'bg-transparent',
                )}
                onClick={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect()

                  setCollapsedFlyout((current) =>
                    current?.key === item.key
                      ? null
                      : {
                          key: item.key,
                          top: getCollapsedFlyoutTop(rect.top, item.children?.length ?? 0),
                        },
                  )
                }}
                type="button"
              >
                {icon}
              </button>
            </li>
          )
        }

        return icon ? (
          <li className="flex h-12 items-center justify-center" key={item.key}>
            <div
              aria-label={label}
              aria-level={2}
              className="flex size-10 items-center justify-center rounded-lg text-[14px] font-semibold leading-5 text-[var(--color-sidebar-text)]"
              role="heading"
            >
              {icon}
            </div>
          </li>
        ) : null
      }

      return hasChildGroup ? (
        <li className={spacingBeforeClassName} key={item.key}>
          <button
            aria-expanded={expanded}
            className="mx-2 flex h-9 max-w-[calc(100%-16px)] cursor-pointer items-center gap-1.5 rounded-md px-3 text-left text-[14px] font-semibold leading-5 text-[var(--color-sidebar-text)] transition-colors hover:text-[var(--color-sidebar-active-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
            onClick={() => {
              setExpandedGroups((groups) => {
                const normalizedGroups = mergeExpandedGroups(items, groups)
                const nextGroups = {
                  ...normalizedGroups,
                  [item.key]: !normalizedGroups[item.key],
                }

                if (expandedGroupsPersistenceKey) {
                  writeExpandedGroups(expandedGroupsPersistenceKey, nextGroups)
                }

                return nextGroups
              })
            }}
            type="button"
          >
            <span className="min-w-0 truncate">{label}</span>
            <span className="flex size-3.5 shrink-0 items-center justify-center">
              <NavChevronIcon open={expanded} strong={portal === 'user'} />
            </span>
          </button>
        </li>
      ) : (
        <li className={spacingBeforeClassName} key={item.key}>
          <div
            aria-level={2}
            className="mx-2 flex h-9 max-w-[calc(100%-16px)] items-center px-3 text-[14px] font-semibold leading-5 text-[var(--color-sidebar-text)]"
            role="heading"
          >
            <span className="min-w-0 truncate">{label}</span>
          </div>
        </li>
      )
    }

    if (collapsed) {
      if (isSubtleItem || isAppItem) {
        return null
      }

      if (hasChildGroup) {
        return (
          <li className="flex h-12 items-center justify-center" key={item.key}>
            <button
              aria-expanded={hasChildren ? collapsedFlyout?.key === item.key : undefined}
              aria-haspopup={hasChildren ? 'menu' : undefined}
              aria-label={label}
              className={cn(
                'flex size-10 items-center justify-center rounded-lg border border-transparent text-[14px] leading-5 text-[var(--color-sidebar-text)] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                isStrongItem ? 'font-semibold' : 'font-normal',
                hasChildren
                  ? 'cursor-pointer hover:bg-[var(--color-sidebar-hover)]'
                  : 'cursor-default',
                active || collapsedFlyout?.key === item.key
                  ? portal === 'user'
                    ? 'sidebar-active-primary'
                    : 'sidebar-active-card-collapsed'
                  : 'bg-transparent',
              )}
              onClick={(event) => {
                if (!hasChildren) {
                  return
                }

                const rect = event.currentTarget.getBoundingClientRect()

                setCollapsedFlyout((current) =>
                  current?.key === item.key
                    ? null
                    : {
                        key: item.key,
                        top: getCollapsedFlyoutTop(rect.top, item.children?.length ?? 0),
                      },
                )
              }}
              type="button"
            >
              {icon}
            </button>
          </li>
        )
      }

      return (
        <li className="flex h-12 items-center justify-center" key={item.key}>
          <a
            aria-label={label}
            className={cn(
              'flex size-10 items-center justify-center rounded-lg border border-transparent text-[14px] leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
              isStrongItem ? '!font-semibold' : 'font-normal',
              active
                ? portal === 'user'
                  ? 'sidebar-active-primary'
                  : 'sidebar-active-card-collapsed'
                : 'bg-transparent',
            )}
            href={item.path ?? item.children?.[0]?.path ?? '#'}
            onClick={(event) => {
              handleLinkClick(event, item.path ?? item.children?.[0]?.path, item.onSelect)
            }}
            rel={item.external ? 'noopener noreferrer' : undefined}
            target={item.external ? '_blank' : undefined}
          >
            {icon}
          </a>
        </li>
      )
    }

    if (item.searchMenu && isProminentItem) {
      if (isGhostProminentItem && portal === 'user') {
        return (
          <li key={item.key}>
            <div
              className={cn(
                'group/search-sidebar-row relative mx-2 grid h-10 w-[calc(100%-16px)] grid-cols-[40px_minmax(0,1fr)_36px] items-center gap-2 rounded-lg text-[15px] font-semibold leading-5 text-[var(--color-sidebar-text)] transition-colors',
                active
                  ? primaryActiveClassName
                  : 'bg-transparent hover:bg-[var(--color-sidebar-hover)]',
              )}
            >
              <a
                className="col-span-3 grid h-full min-w-0 grid-cols-[40px_minmax(0,1fr)_36px] items-center gap-2 rounded-lg pr-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
                href={item.path ?? '#'}
                onClick={(event) => {
                  handleLinkClick(event, item.path, item.onSelect)
                }}
                title={label}
              >
                {reserveIconSpace ? icon : null}
                <span className="min-w-0 flex-1 truncate">{label}</span>
                <span aria-hidden className="size-8" />
              </a>
              <button
                aria-expanded={searchMenu?.key === item.key}
                aria-haspopup="dialog"
                aria-label={item.searchMenu.label}
                className={cn(
                  'absolute right-1 top-1/2 z-10 flex size-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg text-[var(--color-sidebar-text-muted)] transition-colors duration-200 hover:bg-[var(--color-sidebar-hover)] hover:text-[var(--color-sidebar-active-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                  searchMenu?.key === item.key ? 'sidebar-active-soft' : 'bg-transparent',
                )}
                data-sidebar-search-menu-trigger="true"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  const opening = searchMenu?.key !== item.key

                  const placement = getSearchMenuPlacement(
                    event.currentTarget.getBoundingClientRect(),
                    item.searchMenu?.items.length ?? 0,
                  )

                  setSearchMenuValue('')
                  item.searchMenu?.onQueryChange?.('')
                  setSearchMenu(opening ? { key: item.key, ...placement } : null)
                }}
                type="button"
              >
                <MagnifierIcon />
              </button>
            </div>
          </li>
        )
      }

      return (
        <li key={item.key}>
          <div className="mx-2 grid h-10 w-[calc(100%-16px)] grid-cols-[minmax(0,1fr)_40px] items-center gap-1.5">
            <a
              className={cn(
                'grid h-10 min-w-0 items-center rounded-lg border text-[14px] leading-5 text-[var(--color-sidebar-text)] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                reserveIconSpace
                  ? 'grid-cols-[38px_minmax(0,1fr)_18px] gap-[9px]'
                  : 'grid-cols-[minmax(0,1fr)_18px] gap-2 px-3',
                'font-semibold shadow-[0_1px_2px_rgba(26,28,25,0.04)]',
                active
                  ? cn(primaryActiveClassName, 'shadow-none')
                  : 'border-[var(--color-sidebar-border-subtle)] bg-white hover:border-[var(--color-sidebar-border)] hover:bg-white',
              )}
              href={item.path ?? '#'}
              onClick={(event) => {
                handleLinkClick(event, item.path, item.onSelect)
              }}
              title={label}
            >
              {reserveIconSpace ? icon : null}
              <span className="min-w-0 flex-1 truncate">{label}</span>
              <span aria-hidden className="size-[18px]" />
            </a>
            <button
              aria-expanded={searchMenu?.key === item.key}
              aria-haspopup="dialog"
              aria-label={item.searchMenu.label}
              className={cn(
                'flex size-10 cursor-pointer items-center justify-center rounded-lg border text-[var(--color-sidebar-text-muted)] shadow-[0_1px_2px_rgba(26,28,25,0.04)] transition-colors duration-200 hover:border-[var(--color-sidebar-border)] hover:bg-white hover:text-[var(--color-sidebar-active-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                searchMenu?.key === item.key
                  ? 'sidebar-active-soft border-[var(--color-sidebar-border-subtle)] shadow-none'
                  : 'border-[var(--color-sidebar-border-subtle)] bg-white',
              )}
              data-sidebar-search-menu-trigger="true"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                const opening = searchMenu?.key !== item.key

                const placement = getSearchMenuPlacement(
                  event.currentTarget.getBoundingClientRect(),
                  item.searchMenu?.items.length ?? 0,
                )

                setSearchMenuValue('')
                item.searchMenu?.onQueryChange?.('')
                setSearchMenu(opening ? { key: item.key, ...placement } : null)
              }}
              type="button"
            >
              <MagnifierIcon />
            </button>
          </div>
        </li>
      )
    }

    if (hasChildGroup) {
      return (
        <li key={item.key}>
          <button
            aria-expanded={expanded}
            className={cn(
              'mx-2 grid h-9 w-[calc(100%-16px)] cursor-pointer grid-cols-[40px_minmax(0,1fr)_18px] items-center gap-2 rounded-lg text-left text-[14px] font-semibold leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
              item.path && active ? primaryActiveClassName : 'bg-transparent',
            )}
            onClick={() => {
              setExpandedGroups((groups) => {
                const normalizedGroups = mergeExpandedGroups(items, groups)
                const nextGroups = {
                  ...normalizedGroups,
                  [item.key]: !normalizedGroups[item.key],
                }

                if (expandedGroupsPersistenceKey) {
                  writeExpandedGroups(expandedGroupsPersistenceKey, nextGroups)
                }

                return nextGroups
              })
            }}
            type="button"
          >
            {icon}
            <span className="min-w-0 flex-1 truncate">{label}</span>
            <span className="flex size-[18px] items-center justify-center justify-self-end">
              <NavChevronIcon open={expanded} strong={portal === 'user'} />
            </span>
          </button>
          {expanded && hasChildren ? (
            <ul className="grid gap-0">
              {childItems.map((child) => {
                const childActive = child.activeBehavior !== 'never' && activePath === child.path

                return (
                  <li key={child.key}>
                    <a
                      className={cn(
                        'ml-11 mr-2 grid h-9 grid-cols-[minmax(0,1fr)_18px] items-center gap-2 rounded-lg px-3 text-[14px] leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                        child.variant === 'strong' ? 'font-semibold' : 'font-normal',
                        childActive ? secondaryActiveClassName : 'bg-transparent',
                      )}
                      href={child.path}
                      onClick={(event) => {
                        handleLinkClick(event, child.path)
                      }}
                    >
                      <span className="truncate">{child.label}</span>
                      <span aria-hidden className="size-[18px]" />
                    </a>
                  </li>
                )
              })}
            </ul>
          ) : null}
        </li>
      )
    }

    if (isSubtleItem) {
      return (
        <li
          className={cn(
            spacingBeforeClassName,
            item.sticky === 'bottom' ? 'sticky bottom-0 z-10 bg-[var(--color-sidebar-bg)]' : null,
          )}
          key={item.key}
        >
          <div className="group/subtle-sidebar-item relative mx-2">
            <a
              className={cn(
                'grid h-9 grid-cols-[minmax(0,1fr)] items-center rounded-lg text-[14px] leading-5 transition-[background-color,color,padding-right] duration-200 group-hover/subtle-sidebar-item:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                hasActions && inlineStatus
                  ? cn('pl-3 pr-10 group-hover/subtle-sidebar-item:pr-16', {
                      'pr-16': actionMenu?.key === item.key,
                    })
                  : hasActions
                    ? cn('pl-3 pr-3 group-hover/subtle-sidebar-item:pr-10', {
                        'pr-10': actionMenu?.key === item.key,
                      })
                    : inlineStatus
                      ? 'pl-3 pr-10'
                      : 'px-3',
                item.variant === 'subtle-strong'
                  ? 'font-semibold text-[var(--color-sidebar-text)]'
                  : 'font-normal text-[var(--color-sidebar-text)]',
                active ? secondaryActiveClassName : 'bg-transparent',
              )}
              href={item.path ?? '#'}
              onClick={(event) => {
                handleLinkClick(event, item.path, item.onSelect)
              }}
              title={label}
            >
              <span className="min-w-0 truncate">{label}</span>
            </a>
            {inlineStatus ? (
              <span
                aria-label={inlineStatus.label}
                className={cn(
                  'pointer-events-none absolute right-1 top-1/2 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-[var(--color-sidebar-text-muted)] opacity-100 transition-transform duration-200',
                  hasActions
                    ? actionMenu?.key === item.key
                      ? '-translate-x-6'
                      : 'group-hover/subtle-sidebar-item:-translate-x-6'
                    : null,
                )}
                role="img"
              >
                {inlineStatus.icon}
              </span>
            ) : null}
            {hasActions ? (
              <button
                aria-expanded={actionMenu?.key === item.key}
                aria-haspopup="menu"
                aria-label={item.actionsLabel ?? label}
                className={cn(
                  'absolute right-1 top-1/2 z-10 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-[var(--color-sidebar-text-muted)] opacity-0 transition-colors hover:bg-[var(--color-sidebar-hover)] hover:text-[var(--color-sidebar-active-text)] group-hover/subtle-sidebar-item:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                  actionMenu?.key === item.key
                    ? 'bg-[var(--color-sidebar-hover)] opacity-100'
                    : null,
                )}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()

                  const placement = getActionMenuPlacement(
                    event.currentTarget.getBoundingClientRect(),
                    item.actions?.length ?? 0,
                  )

                  setActionMenu((current) =>
                    current?.key === item.key ? null : { key: item.key, ...placement },
                  )
                }}
                data-sidebar-action-menu-trigger="true"
                type="button"
              >
                <MoreHorizontalIcon />
              </button>
            ) : null}
          </div>
        </li>
      )
    }

    if (isAppItem) {
      return (
        <li className={spacingBeforeClassName} key={item.key}>
          <a
            className={cn(
              'mx-2 grid h-[38px] w-[calc(100%-16px)] grid-cols-[40px_minmax(0,1fr)_18px] items-center gap-2 rounded-lg text-[16px] font-medium leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
              active ? primaryActiveClassName : 'bg-transparent',
            )}
            href={item.path ?? '#'}
            onClick={(event) => {
              handleLinkClick(event, item.path, item.onSelect)
            }}
            title={label}
          >
            {icon ?? <span aria-hidden className="size-[18px] justify-self-center" />}
            <span className="min-w-0 truncate">{label}</span>
            <span aria-hidden className="size-[18px]" />
          </a>
        </li>
      )
    }

    return (
      <li key={item.key}>
        <a
          className={cn(
            'mx-2 grid w-[calc(100%-16px)] items-center rounded-lg text-[14px] leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
            reserveIconSpace
              ? isProminentItem
                ? isGhostProminentItem && portal === 'user'
                  ? 'grid-cols-[40px_minmax(0,1fr)_18px] gap-2'
                  : 'grid-cols-[38px_minmax(0,1fr)_18px] gap-[9px]'
                : 'grid-cols-[40px_minmax(0,1fr)_18px] gap-2'
              : 'grid-cols-[minmax(0,1fr)_18px] gap-2 px-3',
            isProminentItem && !(isGhostProminentItem && portal === 'user')
              ? 'my-1 h-10 rounded-[10px] border font-medium'
              : isGhostProminentItem && portal === 'user'
                ? 'h-10 text-[15px] font-semibold'
                : cn('h-9', isStrongItem ? '!font-semibold' : 'font-normal'),
            item.external ? 'pr-3' : null,
            active
              ? isProminentItem && !(isGhostProminentItem && portal === 'user')
                ? cn(primaryActiveClassName, 'shadow-none')
                : isGhostProminentItem && portal === 'user'
                  ? primaryActiveClassName
                  : secondaryActiveClassName
              : isProminentItem && !(isGhostProminentItem && portal === 'user')
                ? 'sidebar-action-button'
                : 'bg-transparent',
          )}
          href={item.path ?? '#'}
          onClick={(event) => {
            handleLinkClick(event, item.path, item.onSelect)
          }}
          rel={item.external ? 'noopener noreferrer' : undefined}
          target={item.external ? '_blank' : undefined}
          title={label}
        >
          {reserveIconSpace ? icon : null}
          <span className="min-w-0 flex-1 truncate">{label}</span>
          <span
            aria-hidden
            className="flex size-[18px] items-center justify-center text-[var(--color-sidebar-text-muted)]"
          >
            {item.external ? <ExternalLinkIcon /> : null}
          </span>
        </a>
      </li>
    )
  }

  const renderItemList = (navItems: SidebarNavItem[]) => {
    const visibleItems = navItems.filter(
      (item) => !item.section || currentExpandedGroups[item.section],
    )

    return visibleItems.length > 0 ? (
      <ul className="grid gap-0">{visibleItems.map(renderNavItem)}</ul>
    ) : null
  }

  const splitStickyItems = (navItems: SidebarNavItem[]) => ({
    bottom: navItems.filter((item) => item.sticky === 'bottom'),
    scroll: navItems.filter((item) => item.sticky !== 'top' && item.sticky !== 'bottom'),
    top: navItems.filter((item) => item.sticky === 'top'),
  })

  if (items.length === 0) {
    return <div className="min-h-0 flex-1" />
  }

  const topItems = usesSplitLayout ? items.slice(0, splitDividerIndex) : items
  const dividerItem = usesSplitLayout ? items[splitDividerIndex] : null
  const bottomItems = usesSplitLayout ? items.slice(splitDividerIndex + 1) : []
  const topSplitItems = splitStickyItems(topItems)
  const bottomSplitItems = splitStickyItems(bottomItems)

  return (
    <div
      className={cn(
        'relative min-h-0 flex-1',
        usesSplitLayout ? 'group/sidebar-split-scroll' : 'group/sidebar-scroll',
      )}
      data-testid={usesSplitLayout ? 'sidebar-split-scroll-frame' : 'sidebar-scroll-frame'}
    >
      {usesSplitLayout ? (
        <nav
          ref={navigationRootRef}
          aria-label="Sidebar navigation"
          className="grid h-full grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] overflow-hidden px-0 py-0"
        >
          <div
            className="group/sidebar-scroll-top flex min-h-0 flex-col overflow-hidden"
            data-testid="sidebar-top-scroll-frame"
          >
            {renderItemList(topSplitItems.top)}
            <div className="relative min-h-0 flex-1">
              <div
                className="sidebar-scroll-area h-full overflow-y-auto overscroll-contain"
                data-testid="sidebar-top-scroll-area"
                onScroll={(event) => {
                  rememberTopScrollPosition(event.currentTarget.scrollTop)
                  syncTopScrollbar()
                }}
                ref={setTopScrollNode}
              >
                {renderItemList(topSplitItems.scroll)}
              </div>
              <SidebarScrollbar collapsed={collapsed} scrollbar={topScrollbar} variant="top" />
            </div>
          </div>
          {dividerItem ? (
            <div aria-hidden data-testid="sidebar-split-divider">
              <div className="mr-[22px] h-px bg-[var(--color-sidebar-border-subtle)]" />
            </div>
          ) : null}
          <div
            className="group/sidebar-scroll-bottom flex min-h-0 flex-col overflow-hidden pt-2"
            data-testid="sidebar-bottom-scroll-frame"
          >
            {renderItemList(bottomSplitItems.top)}
            <div className="relative min-h-0 flex-1">
              <div
                className="sidebar-scroll-area h-full overflow-y-auto overscroll-contain"
                data-testid="sidebar-bottom-scroll-area"
                onScroll={(event) => {
                  rememberBottomScrollPosition(event.currentTarget.scrollTop)
                  syncBottomScrollbar()
                }}
                ref={setBottomScrollNode}
              >
                {renderItemList(bottomSplitItems.scroll)}
              </div>
              <SidebarScrollbar
                collapsed={collapsed}
                scrollbar={bottomScrollbar}
                variant="bottom"
              />
            </div>
            {renderItemList(bottomSplitItems.bottom)}
          </div>
        </nav>
      ) : (
        <nav
          ref={(node) => {
            navigationRootRef.current = node
            setSingleScrollNode(node)
          }}
          aria-label="Sidebar navigation"
          className="sidebar-scroll-area h-full overflow-y-auto overscroll-contain px-0 py-0"
          onScroll={(event) => {
            rememberSingleScrollPosition(event.currentTarget.scrollTop)
            syncSingleScrollbar()
          }}
        >
          {renderItemList(items)}
        </nav>
      )}
      {!usesSplitLayout ? (
        <SidebarScrollbar collapsed={collapsed} scrollbar={singleScrollbar} variant="single" />
      ) : null}
      {flyoutItem && flyoutChildren.length > 0 ? (
        <div
          ref={flyoutRef}
          className={cn(
            'fixed left-16 z-[65] max-w-[calc(100vw-80px)] overflow-hidden rounded-xl border border-[var(--color-sidebar-border-subtle)] bg-white p-1.5 shadow-[0_8px_24px_rgba(26,28,25,0.12)]',
            flyoutItem.flyoutSize === 'wide' ? 'w-[274px]' : 'w-[172px]',
          )}
          data-testid="collapsed-sidebar-flyout"
          role="menu"
          style={{ top: `${String(collapsedFlyout?.top ?? 12)}px` }}
        >
          <div className="flex h-9 items-center px-3 text-[13px] font-medium leading-5 text-[var(--color-sidebar-text-muted)]">
            {flyoutItem.label ?? ''}
          </div>
          <div className="grid gap-0">
            {flyoutChildren.map((child) => {
              const childActive = child.activeBehavior !== 'never' && activePath === child.path

              return (
                <a
                  className={cn(
                    'flex h-9 min-w-0 items-center rounded-lg px-3 text-[14px] leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                    child.variant === 'strong' ? 'font-semibold' : 'font-normal',
                    childActive ? secondaryActiveClassName : 'bg-transparent',
                  )}
                  title={child.label}
                  href={child.path}
                  key={child.key}
                  onClick={(event) => {
                    handleLinkClick(event, child.path)
                    setCollapsedFlyout(null)
                  }}
                  role="menuitem"
                >
                  <span className="block min-w-0 max-w-full truncate">{child.label}</span>
                </a>
              )
            })}
          </div>
        </div>
      ) : null}
      {searchMenu && searchMenuConfig ? (
        <div
          aria-label={searchMenuConfig.label}
          className="fixed z-[70] w-[274px] max-w-[calc(100vw-80px)] overflow-hidden rounded-xl border border-[var(--color-sidebar-border-subtle)] bg-white shadow-[0_8px_24px_rgba(26,28,25,0.12)]"
          data-testid="sidebar-app-search-menu"
          ref={searchMenuRef}
          role="dialog"
          style={{
            left: `${String(searchMenu.left)}px`,
            top: `${String(searchMenu.top)}px`,
          }}
        >
          <label className="grid h-[46px] grid-cols-[20px_minmax(0,1fr)] items-center gap-2 px-3 text-[var(--color-sidebar-text-muted)]">
            <MagnifierIcon />
            <input
              aria-label={searchMenuConfig.inputLabel}
              className="h-full min-w-0 bg-transparent text-[13px] leading-5 text-[var(--color-sidebar-text)] outline-none placeholder:text-[var(--color-text-subtle)]"
              onChange={(event) => {
                const nextValue = event.target.value

                setSearchMenuValue(nextValue)
                searchMenuConfig.onQueryChange?.(nextValue)
              }}
              placeholder={searchMenuConfig.placeholder}
              type="search"
              value={searchMenuValue}
            />
          </label>
          <div className="border-t border-[var(--color-sidebar-border-subtle)] p-1.5">
            {searchMenuConfig.loading ? (
              <div
                className="flex h-12 items-center px-2 text-[13px] leading-5 text-[var(--color-sidebar-text-muted)]"
                role="status"
              >
                {searchMenuConfig.loadingLabel}
              </div>
            ) : filteredSearchItems.length > 0 ? (
              <div
                className="overflow-y-auto"
                role="menu"
                style={{ maxHeight: `${String(sidebarSearchMenuListMaxHeight)}px` }}
              >
                {filteredSearchItems.map((item) => (
                  <a
                    className="grid h-9 min-w-0 grid-cols-[28px_minmax(0,1fr)] items-center rounded-lg px-2 text-[14px] leading-5 text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
                    href={item.path}
                    key={item.key}
                    onClick={(event) => {
                      handleLinkClick(event, item.path, item.onSelect)
                    }}
                    role="menuitem"
                  >
                    <span className="flex size-5 items-center justify-center text-[var(--color-sidebar-text-muted)]">
                      {item.icon}
                    </span>
                    <span className="min-w-0 truncate">{item.label}</span>
                  </a>
                ))}
              </div>
            ) : (
              <div
                className="flex h-12 items-center px-2 text-[13px] leading-5 text-[var(--color-sidebar-text-muted)]"
                role="status"
              >
                {searchTerm ? searchMenuConfig.noMatchesLabel : searchMenuConfig.emptyLabel}
              </div>
            )}
          </div>
        </div>
      ) : null}
      {actionMenu && actionMenuItem && actionMenuActions.length > 0 ? (
        <div
          ref={actionMenuRef}
          className="fixed z-[70] w-[156px] rounded-2xl border border-[var(--color-sidebar-border-subtle)] bg-white p-1.5 shadow-[0_14px_36px_rgba(26,28,25,0.16)]"
          data-testid="sidebar-conversation-action-menu"
          role="menu"
          style={{
            left: `${String(actionMenu.left)}px`,
            top: `${String(actionMenu.top)}px`,
          }}
        >
          {actionMenuActions.map((action) => (
            <button
              className={cn(
                'flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 text-left text-[14px] leading-5 transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                action.tone === 'danger'
                  ? 'text-[var(--color-danger)]'
                  : 'text-[var(--color-sidebar-text)]',
              )}
              key={action.key}
              onClick={() => {
                setActionMenu(null)
                action.onSelect()
              }}
              role="menuitem"
              type="button"
            >
              {action.icon ? (
                <span className="flex size-4 shrink-0 items-center justify-center">
                  {action.icon}
                </span>
              ) : null}
              <span className="min-w-0 truncate">{action.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
